// src/app/pages/app/SettingsPage.jsx
import React, { useEffect, useState } from "react";
import { 
  Download, 
  Bell, 
  Globe, 
  ShieldAlert, 
  Trash2,
  Sparkles,
  Crown,
  CheckCircle2,
  RefreshCw,
  Mail,
  Smartphone,
  Zap,
  AlertCircle,
  Database,
  User,
  Languages
} from "lucide-react";

const KEY = "sct.settings.v1";

function loadSettings() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null") || null;
  } catch {
    return null;
  }
}

function saveSettings(s) {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export default function SettingsPage() {
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const [settings, setSettings] = useState(() => {
    return (
      loadSettings() || {
        language: "",
        notifications: {
          inApp: true,
          email: true,
          sms: false,
        },
        priceAlerts: {
          enabled: true,
          majorMovesOnly: true,
        },
        privacy: {
          analyticsOptOut: false,
          hideProfile: false,
        },
      }
    );
  });

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 1200);
    return () => clearTimeout(t);
  }, [saved]);

  const update = (path, value) => {
    setSettings((prev) => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const onSave = () => {
    setLoading(true);
    setTimeout(() => {
      saveSettings(settings);
      setSaved(true);
      setLoading(false);
    }, 500);
  };

  const exportData = () => {
    const blob = new Blob([JSON.stringify({ settings }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cryptotradex-export.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetSettings = () => {
    if (window.confirm("Reset all settings to default?")) {
      const defaults = {
        language: "",
        notifications: {
          inApp: true,
          email: true,
          sms: false,
        },
        priceAlerts: {
          enabled: true,
          majorMovesOnly: true,
        },
        privacy: {
          analyticsOptOut: false,
          hideProfile: false,
        },
      };
      setSettings(defaults);
      saveSettings(defaults);
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-[#0a0a1a] via-[#0f0f2a] to-[#1a0a2e]">
      {/* Decorative background */}
      <div className="fixed top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-indigo-500/30">
              <Crown size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                Settings
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 border border-indigo-400/20">
                  Preferences
                </span>
              </h1>
              <p className="text-white/50 text-sm">Customize your trading experience</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={resetSettings}
              className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/40 hover:text-white/60 hover:bg-white/10 transition-all duration-300 text-sm flex items-center gap-2"
            >
              <RefreshCw size={14} />
              Reset
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={loading}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold flex items-center gap-2 transition-all duration-300 shadow-lg shadow-indigo-500/25 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <CheckCircle2 size={16} />
                  Saved
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* General */}
          <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:border-white/20 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl" />
            
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Languages size={18} className="text-indigo-400" />
                <div>
                  <div className="text-white font-semibold">General</div>
                  <div className="text-xs text-white/30">Language preferences</div>
                </div>
              </div>

              <div>
                <label className="text-sm text-white/60">Language</label>
                <select
                  value={settings.language}
                  onChange={(e) => update("language", e.target.value)}
                  className="mt-2 w-full rounded-xl bg-[#1a1a3a] border border-white/10 px-4 py-2.5 text-white outline-none focus:border-indigo-400/50 transition-all appearance-none"
                  style={{ color: 'white' }}
                >
                  <option value="" className="text-gray-400">Select language</option>
                  <option value="en-US" className="text-white">English (US)</option>
                  <option value="en-GB" className="text-white">English (UK)</option>
                  <option value="pt" className="text-white">Português</option>
                  <option value="it" className="text-white">Italiano</option>
                  <option value="ru" className="text-white">Русский</option>
                </select>
                <p className="text-xs text-white/20 mt-1">
                  Choose your preferred language. Changes apply after saving.
                </p>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:border-white/20 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl" />
            
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Bell size={18} className="text-purple-400" />
                <div>
                  <div className="text-white font-semibold">Notifications</div>
                  <div className="text-xs text-white/30">Alert preferences</div>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { key: "inApp", label: "In-app notifications", icon: Bell },
                  { key: "email", label: "Email notifications", icon: Mail },
                  { key: "sms", label: "SMS notifications", icon: Smartphone },
                ].map((o) => (
                  <label key={o.key} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all cursor-pointer group">
                    <span className="flex items-center gap-3 text-sm text-white/60 group-hover:text-white/80 transition-colors">
                      <o.icon size={14} className="text-white/20 group-hover:text-white/40" />
                      {o.label}
                    </span>
                    <input
                      type="checkbox"
                      checked={settings.notifications[o.key]}
                      onChange={(e) => update(`notifications.${o.key}`, e.target.checked)}
                      className="h-4 w-4 rounded border-white/20 bg-white/5 text-indigo-500 focus:ring-indigo-500/20 focus:ring-offset-0"
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Price Alerts */}
          <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:border-white/20 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl" />
            
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={18} className="text-emerald-400" />
                <div>
                  <div className="text-white font-semibold">Price Alerts</div>
                  <div className="text-xs text-white/30">Market monitoring</div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all cursor-pointer">
                  <span className="text-sm text-white/60">Enable price alerts</span>
                  <input
                    type="checkbox"
                    checked={settings.priceAlerts.enabled}
                    onChange={(e) => update("priceAlerts.enabled", e.target.checked)}
                    className="h-4 w-4 rounded border-white/20 bg-white/5 text-indigo-500 focus:ring-indigo-500/20 focus:ring-offset-0"
                  />
                </label>

                <label className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all cursor-pointer">
                  <span className="text-sm text-white/60">Major moves only</span>
                  <input
                    type="checkbox"
                    checked={settings.priceAlerts.majorMovesOnly}
                    onChange={(e) => update("priceAlerts.majorMovesOnly", e.target.checked)}
                    className="h-4 w-4 rounded border-white/20 bg-white/5 text-indigo-500 focus:ring-indigo-500/20 focus:ring-offset-0"
                  />
                </label>

                <div className="p-3 rounded-xl bg-white/5 border border-white/5">
                  <div className="text-xs text-white/20 flex items-center gap-2">
                    <AlertCircle size={12} className="text-white/20" />
                    Alert rules will be wired to backend price feed
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy */}
          <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:border-white/20 transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl" />
            
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <ShieldAlert size={18} className="text-blue-400" />
                <div>
                  <div className="text-white font-semibold">Privacy</div>
                  <div className="text-xs text-white/30">Data protection</div>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { key: "analyticsOptOut", label: "Opt out of analytics", icon: Database },
                  { key: "hideProfile", label: "Hide profile from leaderboard", icon: User },
                ].map((o) => (
                  <label key={o.key} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all cursor-pointer group">
                    <span className="flex items-center gap-3 text-sm text-white/60 group-hover:text-white/80 transition-colors">
                      <o.icon size={14} className="text-white/20 group-hover:text-white/40" />
                      {o.label}
                    </span>
                    <input
                      type="checkbox"
                      checked={settings.privacy[o.key]}
                      onChange={(e) => update(`privacy.${o.key}`, e.target.checked)}
                      className="h-4 w-4 rounded border-white/20 bg-white/5 text-indigo-500 focus:ring-indigo-500/20 focus:ring-offset-0"
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Data Export & Account - Full Width */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Data Export */}
            <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:border-white/20 transition-all duration-300">
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <Download size={18} className="text-cyan-400" />
                  <div>
                    <div className="text-white font-semibold">Data Export</div>
                    <div className="text-xs text-white/30">Export your data</div>
                  </div>
                </div>
                
                <button
                  type="button"
                  onClick={exportData}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Download size={16} />
                  Export Settings
                </button>
                
                <div className="text-xs text-white/20 mt-2 text-center">
                  Exports local settings; future: trades, chats, and account events
                </div>
              </div>
            </div>

            {/* Account Deletion */}
            <div className="relative overflow-hidden rounded-2xl bg-rose-500/10 backdrop-blur-sm border border-rose-500/20 p-6 hover:border-rose-500/30 transition-all duration-300">
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <Trash2 size={18} className="text-rose-400" />
                  <div>
                    <div className="text-white font-semibold">Account Deletion</div>
                    <div className="text-xs text-rose-300/50">Permanently delete account</div>
                  </div>
                </div>
                
                <button
                  type="button"
                  className="w-full px-4 py-3 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 text-rose-300 font-semibold flex items-center justify-center gap-2 cursor-not-allowed opacity-60 transition-all duration-300"
                  disabled
                >
                  <Trash2 size={16} />
                  Request Deletion
                </button>
                
                <div className="text-xs text-rose-300/30 mt-2 text-center">
                  This is a placeholder until backend deletion workflow is implemented
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <span className="flex items-center justify-center gap-2 text-xs text-white/20">
            <Sparkles size={12} className="text-indigo-400/50" />
            Your settings are stored locally and synced across sessions
          </span>
        </div>
      </div>
    </div>
  );
}
